import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { IAsset, MIN_DEFAULT_SCREEN_SIZE, VISIBLE_TRACK_DEFAULT_WIDTH } from '../../constants/assets';
import { CarouselButtonSide } from '../../types/assets';
import { isNotEndOfTrack } from '../../utils/assets';
import { MoreItem } from '../BottomNavigation/MoreItem';
import { StyledCarouselTrack } from '../Styled/StyledCarousel';
import { CarouselButton } from './CarouselButton';

interface IAssetsCarousel {
  assets: IAsset[];
  windowWidth: number;
  handleShowVideo: (asset: IAsset) => void;
}

export const AssetsCarousel = (props: IAssetsCarousel): JSX.Element => {
  const { assets, windowWidth, handleShowVideo } = props;

  const [trackWidth, setTrackWidth] = useState(0);
  const [visibleTrackPart, setVisibleTrackPart] = useState(0);
  const trackRef = useRef() as MutableRefObject<HTMLDivElement>;

  const isDefaultSize = windowWidth >= MIN_DEFAULT_SCREEN_SIZE;
  const visibleTrackWidth = isDefaultSize ? VISIBLE_TRACK_DEFAULT_WIDTH : windowWidth - 44;

  const isPrevBtnDisabled = visibleTrackPart >= 0;
  const isNextBtnDisabled = Math.abs(visibleTrackPart) + visibleTrackWidth === trackWidth;

  useEffect(() => {
    if (trackRef.current) {
      const { offsetWidth } = trackRef.current;
      setTrackWidth(offsetWidth);
    }
  }, [windowWidth]);

  const handlePrevBtnClick = () => {
    if (visibleTrackPart >= 0) return;

    if (Math.abs(visibleTrackPart) >= visibleTrackWidth) {
      setVisibleTrackPart(prevValue => prevValue + visibleTrackWidth);
    } else {
      setVisibleTrackPart(0);
    }
  };

  const handleNextBtnClick = () => {
    if (Math.abs(visibleTrackPart) >= trackWidth) return;

    if (isNotEndOfTrack(trackWidth, visibleTrackPart, visibleTrackWidth)) {
      setVisibleTrackPart(prevValue => prevValue - visibleTrackWidth);
    } else {
      setVisibleTrackPart(-(trackWidth - visibleTrackWidth));
    }
  };

  return (
    <>
      <CarouselButton
        iconSide={CarouselButtonSide.left}
        handleBtnClick={handlePrevBtnClick}
        isDisabled={isPrevBtnDisabled}
      />
      <StyledCarouselTrack
        ref={trackRef}
        isDefaultMargin={isDefaultSize}
        visiblePart={visibleTrackPart}
        data-testid="carousel-track"
      >
        {assets.map((asset: IAsset, index: number) => (
          <MoreItem key={index} asset={asset} onShowVideo={handleShowVideo} />
        ))}
      </StyledCarouselTrack>
      <CarouselButton
        iconSide={CarouselButtonSide.right}
        handleBtnClick={handleNextBtnClick}
        isDisabled={isNextBtnDisabled}
      />
    </>
  );
};
